// Layout tokens for the content-page block system: widths, reflow points,
// spacing and image geometry. theme.js owns the brand tokens (color, font,
// type scale).
//
// Page files in src/content/pages declare intent ('dense' gallery, 'warm'
// band); this file decides what that looks like. Keep raw pixel values out of
// page data so a change here lands everywhere.

import { COLORS, FONTS, TYPE } from './theme';

// Viewport widths at which a block changes column count — named by role, not
// by device.
export const BREAKPOINTS = {
  stack: 600, // steps: stacked → 2-up
  pair: 640,  // textCards: cards 1-up → 2-up
  band: 768,  // split / quote / faq: stacked → two columns
  form: 900,  // formBand: stacked → copy beside form
  wide: 1000, // textCards / listRow: stacked → multi-column
  row: 1024,  // steps: 2-up → 4-up
};

export const MQ = Object.fromEntries(
  Object.entries(BREAKPOINTS).map(([name, px]) => [name, `(min-width: ${px}px)`]),
);

/** The gutter every band holds against the viewport edge. */
export const PAD = 28;

// Container widths. `prose` is the reading measure; the rest are bands that
// deliberately run wider than the copy.
export const WIDTH = {
  prose: 860,   // heading, text, list, cta, faq
  narrow: 1000, // imageText, cards, linkCards
  quote: 1060,  // quote with a portrait
  band: 1120,   // split
  wide: 1280,   // textCards, listRow, formBand
  full: 1500,   // steps, bleed gallery
  single: 660,  // a gallery holding one photo
  stack: 560,   // any band that has collapsed to one column
  card: 460,    // a product card standing alone
  measure: 760, // centered pull-quote / hero subtitle
};

/** Vertical rhythm. Bands are the tall sections; blocks are the inline ones. */
export const SPACE = {
  bandY: 72,    // steps, bleed gallery, formBand
  bandTop: 56,  // air above a tinted band, so the tint doesn't clip the block above
  blockY: 40,   // split, gallery, textCards
  colGap: 56,   // between the two columns of a band
  groupGap: 28, // between heading/paragraph groups inside a column
};

export const RADIUS = {
  band: 14, // full-width photos and cards
  tile: 12, // grid tiles
};

/** Card and rule surfaces — the warm hairline used by cards, FAQs and borders. */
export const SURFACE = {
  cardBg: '#fff',
  border: '#e7e0d0',
  rule: '1px solid #e7e0d0',
};

// The site's one button: amber, with a charcoal label. Not white — white on
// this amber is about 1.75:1, which fails contrast for button text.
export const BUTTON = {
  display: 'inline-block', padding: '14px 32px', background: COLORS.orange, color: COLORS.dark,
  textDecoration: 'none', borderRadius: '10px', fontWeight: 600, fontSize: TYPE.button,
  fontFamily: FONTS.sans,
};

/** Secondary action beside a primary — reads as a button without competing. */
export const BUTTON_OUTLINE = {
  ...BUTTON, background: 'transparent', color: COLORS.dark, border: `1.5px solid ${COLORS.dark}`,
};

/** Charcoal fill, for the rare CTA that sits on amber and can't be amber itself. */
export const BUTTON_DARK = {
  ...BUTTON, background: COLORS.dark, color: '#fff',
};

// Two overlapping frames: back high-right, front low-left and larger, so the
// pair reads as one composition rather than two tiles. `circle` is the
// original portrait treatment; `rect` is the same idea with rounded-corner
// photos instead of discs.
export const COLLAGE = {
  circle: {
    maxWidth: 460,
    back: { top: 0, right: 0, width: '58%' },
    front: { bottom: 0, left: 0, width: '66%' },
    radius: '50%',
    aspect: '1 / 1',
  },
  // Same-size frames, just offset — reads as a pair of snapshots rather than
  // the circle treatment's one-dominant-one-accent composition.
  rect: {
    maxWidth: 820,
    back: { top: '-10%', right: 0, width: '60%' },
    front: { bottom: '-10%', left: '-10%', width: '60%' },
    radius: RADIUS.band,
    aspect: '4 / 5'
  },
};

/** Resolve a collage's `variant` name, falling back to the circle treatment. */
export const collageOf = (name) => COLLAGE[name] || COLLAGE.circle;

/** Amber rule under a word inside a heading — the `mark` inline segment. */
export const MARK = {
  textDecoration: 'underline',
  textDecorationColor: COLORS.orange,
  textDecorationThickness: '3px',
  textUnderlineOffset: '5px',
};

/** Section tint. Blocks set `tone: 'warm'` rather than naming a color. */
export const TONE = {
  none: 'transparent',
  warm: COLORS.creamAlt,
  cream: COLORS.cream,
};

export const toneOf = (name, fallback = 'none') => TONE[name] || TONE[fallback];

// Image geometry. Every image size on the content pages resolves here; a page
// asks for a variant by name and this decides what it looks like.
export const MEDIA = {
  hero: { minHeight: '38vh', overlay: 'rgba(0,0,0,0.38)' },

  // `fit: 'contain'` / `stretch: false` for graphics carrying words (a
  // testimonial card, a poster, a menu), which must never be cropped.
  split: {
    default: { radius: RADIUS.band, maxHeightNarrow: 420, shadow: null, fit: 'cover', stretch: true },
    raised: {
      radius: RADIUS.band, maxHeightNarrow: 420, fit: 'cover', stretch: true,
      shadow: '0 12px 30px rgba(0,0,0,0.08)',
    },
    whole: { radius: RADIUS.band, maxHeightNarrow: 420, shadow: null, fit: 'contain', stretch: false },
    // A portrait cropped to a disc, for a "meet your expert" band.
    circle: {
      radius: '50%', maxHeightNarrow: 420, shadow: null, fit: 'cover', stretch: false,
      aspect: '1 / 1', maxWidth: 420,
    },
  },

  // Legacy background-image band, retired in phase 3.
  imageText: { minHeight: 320, radius: RADIUS.band },

  steps: {
    default: { aspect: '3 / 2', width: '100%', maxWidth: null, radius: RADIUS.tile, gapBelow: 18 },
    // Inset from the column so the roundels don't read as touching discs.
    portraits: { aspect: '1 / 1', width: '78%', maxWidth: 360, radius: '50%', gapBelow: 34 },
  },

  card: {
    default: { aspect: '4 / 3', chrome: true, align: 'center' },
    square: { aspect: '1 / 1', chrome: true, align: 'center' },
    // No panel or border: photo, title and price sit straight on the page.
    plain: { aspect: '1 / 1', chrome: false, align: 'left' },
  },

  gallery: {
    default: { tile: 200, gap: 20, aspect: '1 / 1', radius: RADIUS.tile, maxColumns: 7 },
    dense: { tile: 175, gap: 10, aspect: '1 / 1', radius: RADIUS.tile, maxColumns: 7 },
    // Edge-to-edge editorial strip. Frames shrink to share the row down to
    // `minTile`; below that the strip overflows and scrolls (phones).
    bleed: { tile: 280, minTile: 200, gap: 0, aspect: '4 / 5', radius: 0, bleed: true, maxWidth: '62vw' },
  },

  quote: {
    default: { column: 320, radius: RADIUS.band, aspect: null },
    portrait: { column: 320, radius: '50%', aspect: '1 / 1' },
  },
};

// Organic amber panels. The radii are cycled so a row reads as hand-drawn
// rather than repeated; the last is a plain circle.
export const SHAPE = {
  // Copy that outgrows the panel pushes it tall and the blob reads as an
  // ellipse. 320 fits the longest weekday; below ~280 it no longer does.
  size: 320,
  gap: 26,
  // The silhouette curves away top and bottom, so copy needs more room there.
  padY: '12%',
  padX: '10%',
  fill: COLORS.orange,
  blobs: [
    '62% 38% 46% 54% / 54% 45% 55% 46%',
    '38% 62% 63% 37% / 41% 44% 56% 59%',
    '54% 46% 38% 62% / 49% 70% 30% 51%',
    '45% 55% 57% 43% / 63% 40% 60% 37%',
    '50%',
  ],
};

/** Resolve a block's `variant` against a MEDIA family, falling back to default. */
export const variantOf = (family, name) => MEDIA[family][name] || MEDIA[family].default;

/** Width a gallery row needs to hold `n` tiles at full size, gutters included. */
export const galleryRowWidth = (n, { tile, gap }) => n * tile + (n - 1) * gap + PAD * 2;

/** The 860px reading measure, as a style object. */
export const WRAP = { maxWidth: `${WIDTH.prose}px`, margin: '0 auto', padding: `0 ${PAD}px` };

/** A centered band container at an arbitrary width. */
export const container = (maxWidth) => ({
  maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
  margin: '0 auto',
  padding: `0 ${PAD}px`,
});
